/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/pages/auth/AuthPage`, `src/pages/home/HomePage`).
 */

import { useEffect } from "react";
import { Navigate, Route, Routes as RouterRoutes } from "react-router-dom";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useLastLocation } from "./LastLocationProvider";
import HomePage from "../pages/home/HomePage";
import ErrorsPage from "../pages/errors/ErrorsPage";
import LogoutPage from "../pages/auth/Logout";
import { LayoutContextProvider } from "../../_metronic";
import Layout from "../../_metronic/layout/Layout";
import * as routerHelpers from "../router/RouterHelpers";
import AuthPage from "../pages/auth/AuthPage";
import * as auth from "../store/ducks/auth.duck";

export const Routes = () => {
  const dispatch = useDispatch();
  const lastLocation = useLastLocation();
  routerHelpers.saveLastLocation(lastLocation);
  const { isAuthorized, authToken, menuConfig, userLastLocation } = useSelector(
    ({ auth, builder: { menuConfig } }) => ({
      menuConfig,
      isAuthorized: auth.user != null,
      authToken: auth.authToken,
      userLastLocation: routerHelpers.getLastLocation()
    }),
    shallowEqual
  );

  // Check for persisted authToken on app startup
  useEffect(() => {
    // If we have a persisted token but no user data, fetch the user
    if (authToken && !isAuthorized) {
      dispatch(auth.actions.requestUser());
    }
  }, [authToken, isAuthorized, dispatch]);

  return (
    /* Create `LayoutContext` from current `menuConfig`. */
    <LayoutContextProvider menuConfig={menuConfig}>
      <RouterRoutes>
        {!isAuthorized ? (
          /* Render auth page when user at `/auth` and not authorized. */
          <Route path="/auth/*" element={<AuthPage />} />
        ) : (
          /* Otherwise redirect to root page (`/`) */
          <Route path="/auth/*" element={<Navigate to={userLastLocation} replace />} />
        )}

        <Route path="/error" element={<ErrorsPage />} />
        <Route path="/logout" element={<LogoutPage />} />

        {!isAuthorized ? (
          /* Redirect to `/auth` when user is not authorized */
          <Route path="*" element={<Navigate to="/auth/login" replace />} />
        ) : (
          <Route path="*" element={
            <Layout>
              <HomePage userLastLocation={userLastLocation} />
            </Layout>
          } />
        )}
      </RouterRoutes>
    </LayoutContextProvider>
  );
};
